import React, { useRef, useState, useEffect } from "react";
import { useGlobalContext } from "../../context/stateContext";
import { useAuth } from "../../context/Auth";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { RotatingLines } from "react-loader-spinner";
import {
  Package,
  Search,
  Plus,
  Trash2,
  Save,
  ChevronDown,
  ChevronUp,
  FileText,
  Calendar,
  DollarSign,
  Hash,
} from "lucide-react";
import { showApiErrors } from "../../utils/helpers";
import SearchableSupplierDropdown from "../../components/common/SearchableSupplierDropdown";

const NewStockInvoice = () => {
  const searchRef = useRef();
  const debounceTimerRef = useRef(null);
  const { requestWithAuth } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const { activeSession } = useGlobalContext();
  const [drugsDropdown, setDrugsDropdown] = useState(true);
  const [drugs, setDrugs] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [gettingSuppliers, setGettingSuppliers] = useState(false);
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [insertedDrugs, setInsertedDrugs] = useState([]);
  const [totalCost, setTotalCost] = useState(0.0);
  const [newStockInvoiceData, setNewStockInvoiceData] = useState(null);
  const [unitPrice, setUnitPrice] = useState("");
  const [batches, setBatches] = useState([{ quantity: "", expiryDate: "" }]);
  const [recording, setRecording] = useState(false);
  const [selectedResultIndex, setSelectedResultIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const listContainerRef = useRef(null);
  const quantityRef = useRef(null);

  useEffect(() => {
    if (selectedDrug) {
      setTimeout(() => {
        quantityRef.current?.focus();
      }, 100);
    }
  }, [selectedDrug]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        recordInvoice();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [newStockInvoiceData, insertedDrugs]);

  const selectDrug = (drug) => {
    setSelectedDrug(drug);
    setUnitPrice(drug?.drug?.cost_price || "");
    setBatches([{ quantity: "", expiryDate: "" }]);
    setDrugsDropdown(false);
    setSearch("");
    setDrugs([]);
  };

  const handleChange = (e) => {
    setNewStockInvoiceData({
      ...newStockInvoiceData,
      [e.target.name]: e.target.value,
    });
  };

  const setSupplier = (value) => {
    setNewStockInvoiceData({
      ...newStockInvoiceData,
      supplier: value,
    });
  };

  const getDrugs = async (search_query) => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/inventory/branch/${activeSession?.branch?.id}?paginated=false&search=${search_query}`,
        "GET"
      );
      setDrugs(data);
    } catch (error) {
      console.error("Error fetching drugs data:", error);
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = (value) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (value.length >= 3) {
        getDrugs(value);
      }
    }, 500);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearch(value);
  };

  const insertDrug = () => {
    if (!unitPrice || parseFloat(unitPrice) <= 0) {
      return toast.error("Please specify a valid unit price");
    }
    
    if (selectedDrug) {
      // Validate all batches
      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        if (!batch.quantity || parseFloat(batch.quantity) <= 0) {
          return toast.error(`Please enter a valid quantity for batch ${i + 1}`);
        }
        if (!batch.expiryDate) {
          return toast.error(`Please specify the expiry date for batch ${i + 1}`);
        }
      }

      let newInsertedDrugs = [...insertedDrugs];

      for (const batch of batches) {
        const existingDrugIndex = newInsertedDrugs.findIndex(
          (item) => item.drug === selectedDrug?.drug?.id && item.expiry_date === batch.expiryDate
        );

        if (existingDrugIndex !== -1) {
          toast.error(
            `${selectedDrug?.drug?.name} with expiry date ${batch.expiryDate} is already in the cart. Please edit the quantity in the cart instead.`
          );
          return;
        }

        const total_cost = parseFloat(unitPrice) * parseFloat(batch.quantity);
        newInsertedDrugs.push({
          drug: selectedDrug?.drug?.id,
          drug_name: selectedDrug?.drug?.name,
          quantity: batch.quantity,
          expiry_date: batch.expiryDate,
          unit_price: unitPrice,
          total_cost: total_cost.toFixed(2),
        });
      }

      setInsertedDrugs(newInsertedDrugs);
      setBatches([{ quantity: "", expiryDate: "" }]);
      setSelectedDrug(null);
      setUnitPrice("");
      setDrugsDropdown(true);

      let total = 0;
      newInsertedDrugs.forEach((item) => {
        total += parseFloat(item?.total_cost);
      });
      setTotalCost(total.toFixed(2));
    } else {
      toast.error("Select a drug first");
    }
  };

  const removeInsertedDrug = (index) => {
    const newInsertedDrugs = [...insertedDrugs];
    newInsertedDrugs.splice(index, 1);
    setInsertedDrugs(newInsertedDrugs);
    let total = 0;
    newInsertedDrugs.map((item) => {
      total += parseFloat(item?.total_cost);
    });
    setTotalCost(total.toFixed(2));
  };

  const updateInsertedDrug = (index, field, value) => {
    if (field === "quantity" && (!value || parseFloat(value) <= 0)) {
      return toast.error("Please enter a valid quantity");
    }
    if (field === "unit_price" && (!value || parseFloat(value) <= 0)) {
      return toast.error("Please enter a valid unit price");
    }

    const newInsertedDrugs = [...insertedDrugs];
    newInsertedDrugs[index][field] = value;

    // Recalculate total cost for this item
    const unitPrice = parseFloat(newInsertedDrugs[index].unit_price);
    const quantity = parseFloat(newInsertedDrugs[index].quantity);
    newInsertedDrugs[index].total_cost = (unitPrice * quantity).toFixed(2);

    setInsertedDrugs(newInsertedDrugs);

    // Recalculate overall total
    let total = 0;
    newInsertedDrugs.forEach((item) => {
      total += parseFloat(item?.total_cost);
    });
    setTotalCost(total.toFixed(2));
  };

  const getSuppliers = async () => {
    setGettingSuppliers(true);
    try {
      const data = await requestWithAuth(`/supplier?paginated=false`, "GET");
      setSuppliers(data);
    } catch (error) {
      console.error("Error fetching suppliers data:", error);
    } finally {
      setGettingSuppliers(false);
    }
  };

  const recordInvoice = async () => {
    try {
      if (
        !newStockInvoiceData ||
        !newStockInvoiceData.supplier ||
        !newStockInvoiceData.invoice_date ||
        !newStockInvoiceData.invoice_number ||
        !newStockInvoiceData.invoice_amount ||
        !newStockInvoiceData.invoice_narration
      ) {
        return toast.error("Please fill all required fields");
      }
      setRecording(true);
      const body = JSON.stringify({
        ...newStockInvoiceData,
        drugs: insertedDrugs,
      });

      const data = await requestWithAuth(`/supplier/invoice/`, "POST", body, {
        "Content-Type": "application/json",
      });
      Swal.fire({
        icon: "success",
        title: "Success",
        text: data?.message || "Invoice created successfully",
      }).then(() => {
        window.location.href = "/inventory/invoice";
      });
    } catch (error) {
      console.log(error);
      showApiErrors(error);
    } finally {
      setRecording(false);
    }
  };

  useEffect(() => {
    if (drugsDropdown) {
      // Small timeout to ensure element is rendered
      setTimeout(() => {
        searchRef.current?.focus();
      }, 0);
    } else {
      setSelectedResultIndex(-1);
    }
  }, [drugsDropdown]);

  const handleSearchKeyDown = (e) => {
    if (!drugs.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedResultIndex((prev) =>
        prev < drugs.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedResultIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedResultIndex >= 0 && selectedResultIndex < drugs.length) {
        selectDrug(drugs[selectedResultIndex]);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDrugsDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (selectedResultIndex >= 0 && listContainerRef.current) {
      const selectedElement = listContainerRef.current.querySelector(
        `#drug-result-${selectedResultIndex}`
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [selectedResultIndex]);

  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
    getSuppliers();

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Inventory</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">New Invoice</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <FileText size={32} />
                Supplier Invoice Transaction
              </h1>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
              <p className="text-sm text-gray-600">Branch</p>
              <p className="text-lg font-semibold text-gray-900">
                {activeSession?.branch?.name}
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Panel - Invoice Entry */}
          <div className="lg:col-span-2 space-y-6">
            {/* Invoice Details Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <FileText className="text-blue-500" size={24} />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Invoice Details
                    </h2>
                    <p className="text-sm text-gray-500">
                      Enter supplier invoice information
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <SearchableSupplierDropdown
                    suppliers={suppliers}
                    value={newStockInvoiceData?.supplier}
                    onChange={(value) => setSupplier(value)}
                    label="Supplier *"
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Invoice Date *
                    </label>
                    <input
                      type="date"
                      name="invoice_date"
                      required
                      onChange={(e) => handleChange(e)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Invoice Number *
                    </label>
                    <input
                      type="text"
                      name="invoice_number"
                      required
                      onChange={(e) => handleChange(e)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Invoice Amount *
                    </label>
                    <input
                      type="text"
                      name="invoice_amount"
                      required
                      onChange={(e) => handleChange(e)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Invoice Narration *
                    </label>
                    <select
                      required
                      onChange={(e) => handleChange(e)}
                      name="invoice_narration"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select Narration</option>
                      <option value="Supply">Supply</option>
                      <option value="Credit Note">Credit Note</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Drug Selection Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <Package className="text-blue-500" size={24} />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Drug Details
                    </h2>
                    <p className="text-sm text-gray-500">
                      Search and add drugs to invoice
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {/* Drug Search */}
                <div className="relative" ref={dropdownRef}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search Drug
                  </label>
                  <div
                    className="relative cursor-pointer border border-gray-300 rounded-lg px-4 py-2.5 text-sm flex justify-between items-center"
                    onClick={() => {
                      setDrugsDropdown(!drugsDropdown);
                    }}
                  >
                    <span
                      className={
                        selectedDrug?.drug?.name
                          ? "text-gray-900"
                          : "text-gray-400"
                      }
                    >
                      {selectedDrug?.drug?.name || "Select a drug..."}
                    </span>
                    {drugsDropdown ? (
                      <ChevronUp size={20} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400" />
                    )}
                  </div>

                  {drugsDropdown && (
                    <div
                      className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg"
                    >
                      <div className="p-3 border-b border-gray-200">
                        <div className="relative">
                          <Search
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                            size={18}
                          />
                          <input
                            ref={searchRef}
                            value={search}
                            onChange={handleSearchChange}
                            onKeyDown={handleSearchKeyDown}
                            type="text"
                            placeholder="Type to search..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="max-h-64 overflow-y-auto" ref={listContainerRef}>
                        {search?.length < 3 ? (
                          <p className="p-4 text-sm text-gray-500 text-center">
                            Please enter at least 3 characters
                          </p>
                        ) : loading ? (
                          <p className="p-4 text-sm text-gray-500 text-center">
                            Searching...
                          </p>
                        ) : drugs?.length > 0 ? (
                          drugs.map((drug_item, idx) => (
                            <div
                              key={idx}
                              id={`drug-result-${idx}`}
                              onClick={() => selectDrug(drug_item)}
                              className={`p-3 cursor-pointer border-b border-gray-100 last:border-0 ${idx === selectedResultIndex
                                ? "bg-blue-200"
                                : "hover:bg-blue-300"
                                }`}
                            >
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-900">
                                  {drug_item?.drug?.name}
                                </span>
                                <span className="text-sm font-semibold text-blue-600">
                                  GHS {drug_item?.drug?.cost_price}
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="p-4 text-sm text-gray-500 text-center">
                            No results found
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Drug Details Grid */}
                {selectedDrug?.drug && (
                  <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <DollarSign size={14} className="inline mr-1" />
                        Unit Price *
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">
                          GHS
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={unitPrice}
                          onChange={(e) => setUnitPrice(e.target.value)}
                          placeholder="0.00"
                          className="w-full border border-gray-300 rounded-lg pl-14 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div className="col-span-2 space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className="text-sm font-semibold text-gray-700">Batches</h3>
                        <button
                          type="button"
                          onClick={() => setBatches([...batches, { quantity: "", expiryDate: "" }])}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                        >
                          <Plus size={14} />
                          Add Batch
                        </button>
                      </div>
                      
                      {batches.map((batch, index) => (
                        <div key={index} className="grid grid-cols-12 gap-3 items-start p-3 bg-white border border-gray-200 rounded-lg">
                          <div className="col-span-5">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Quantity *
                            </label>
                            <input
                              type="number"
                              min="1"
                              step="1"
                              value={batch.quantity}
                              onChange={(e) => {
                                const newBatches = [...batches];
                                newBatches[index].quantity = e.target.value;
                                setBatches(newBatches);
                              }}
                              onWheel={(e) => e.target.blur()}
                              className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              ref={index === 0 ? quantityRef : null}
                            />
                          </div>
                          <div className="col-span-6">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Expiry Date *
                            </label>
                            <input
                              type="date"
                              value={batch.expiryDate}
                              onChange={(e) => {
                                const newBatches = [...batches];
                                newBatches[index].expiryDate = e.target.value;
                                setBatches(newBatches);
                              }}
                              className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          {batches.length > 1 && (
                            <div className="col-span-1 pt-6 text-right">
                              <button
                                type="button"
                                onClick={() => {
                                  const newBatches = [...batches];
                                  newBatches.splice(index, 1);
                                  setBatches(newBatches);
                                }}
                                className="text-red-500 hover:text-red-700 p-1"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {batches.some(b => b.quantity) && unitPrice && (
                      <div className="col-span-2 pt-2 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-600">
                            Total Cost:
                          </span>
                          <span className="text-lg font-bold text-blue-600">
                            GHS{" "}
                            {(
                              batches.reduce((acc, curr) => acc + (parseFloat(curr.quantity) || 0), 0) *
                              parseFloat(unitPrice)
                            ).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Add Button */}
                <button
                  onClick={insertDrug}
                  disabled={
                    !selectedDrug?.drug ||
                    !unitPrice ||
                    batches.some(b => !b.quantity || !b.expiryDate)
                  }
                  className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-sm flex items-center justify-center gap-2"
                >
                  <Plus size={20} />
                  Add to Invoice
                </button>
              </div>
            </div>
          </div>

          {/* Right Panel - Invoice Items */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 sticky top-6">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Package size={20} />
                  Invoice Items ({insertedDrugs.length})
                </h2>
              </div>

              <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
                {insertedDrugs.length > 0 ? (
                  <div className="divide-y divide-gray-200">
                    {insertedDrugs.map((drug, index) => (
                      <div key={index} className="p-4 hover:bg-gray-50">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <h3 className="text-sm font-medium text-gray-900">
                              {drug?.drug_name}
                            </h3>
                            <p className="text-xs text-gray-500 mt-1">
                              ID: {drug?.drug}
                            </p>
                            <p className="text-xs text-gray-500">
                              Exp:{" "}
                              {new Date(drug?.expiry_date).toLocaleDateString()}
                            </p>
                          </div>
                          <button
                            onClick={() => removeInsertedDrug(index)}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>

                        <div className="space-y-2 mt-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 w-16">
                              Qty:
                            </span>
                            <input
                              type="number"
                              min="1"
                              value={drug?.quantity}
                              onChange={(e) =>
                                updateInsertedDrug(
                                  index,
                                  "quantity",
                                  e.target.value
                                )
                              }
                              onWheel={(e) => e.target.blur()}
                              className="w-20 border border-gray-300 rounded px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 w-16">
                              Price:
                            </span>
                            <div className="relative flex-1">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                                GHS
                              </span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={drug?.unit_price}
                                onChange={(e) =>
                                  updateInsertedDrug(
                                    index,
                                    "unit_price",
                                    e.target.value
                                  )
                                }
                                className="w-full border border-gray-300 rounded pl-12 pr-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                          <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                            <span className="text-xs font-medium text-gray-600">
                              Total:
                            </span>
                            <span className="text-sm font-semibold text-gray-900">
                              GHS {drug?.total_cost}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <Package size={48} className="mx-auto text-gray-300 mb-3" />
                    <p className="text-sm text-gray-500">No items added</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Add drugs to create invoice
                    </p>
                  </div>
                )}
              </div>

              {insertedDrugs.length > 0 && (
                <div className="border-t border-gray-200">
                  <div className="p-6 bg-gray-50">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-lg font-semibold text-gray-900">
                        Total
                      </span>
                      <span className="text-2xl font-bold text-blue-600">
                        GHS {totalCost}
                      </span>
                    </div>

                    {/* Record Invoice Button */}
                    <button
                      onClick={recordInvoice}
                      disabled={recording}
                      className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-sm flex items-center justify-center gap-2"
                    >
                      {recording ? (
                        <>
                          <RotatingLines
                            visible={true}
                            height="20"
                            width="20"
                            strokeColor="white"
                            strokeWidth="5"
                            animationDuration="0.75"
                            ariaLabel="rotating-lines-loading"
                          />
                          Recording Invoice...
                        </>
                      ) : (
                        <>
                          <Save size={20} />
                          Record Invoice (Alt+S)
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewStockInvoice;
